import { QueryCache, QueryClient } from '@tanstack/react-query';

import { captureExceptionSafely } from '@/helpers/capture-exception-safely';

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: err => {
      captureExceptionSafely(err);
    },
  }),
  defaultOptions: {
    mutations: {
      onError: err => {
        captureExceptionSafely(err);
      },
    },
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 2,
    },
  },
});

export enum QueryKey {
  Account = 'account',
  ActiveProjects = 'active-projects',
  ActivityStatuses = 'activity-statuses',
  AskToJoinLink = 'ask-to-join-link',
  CompanySavvyCrunchbase = 'company-savvy-crunchbase',
  Companies = 'companies',
  CompaniesKpi = 'companies-kpi',
  CompaniesSearchCrunchbase = 'companies-search-crunchbase',
  CompaniesSearchSuggestions = 'companies-search-suggestions',
  CompaniesSearchSavvy = 'companies-search-savvy',
  CompaniesStatistics = 'companies-statistics',
  CommentLink = 'comment-link',
  Company = 'company',
  CompanyActionsInfo = 'company-actions-info',
  CompanyExternalLinks = 'company-external-links',
  CompanyComments = 'company-comments',
  CompanyDomainAvailability = 'company-domain-availability',
  CompanyClients = 'company-clients',
  CompanyContacts = 'company-contacts',
  CompanyFileScanningStatus = 'company-file-scanning-status',
  CompanyFreeTextFields = 'company-free-text-fields',
  CompanyInvestors = 'company-investors',
  CompanyFiles = 'company-files',
  CompanyFundings = 'company-fundings',
  CompanyMeetings = 'company-meetings',
  CompanyNews = 'company-news',
  CompanyOfficeLocations = 'company-office-locations',
  CompanyOptionsCount = 'company-options-count',
  CompanyProjectsListings = 'company-projects-listings',
  CompanyProducts = 'company-products',
  CompanyRankings = 'company-ratings',
  CompanyTags = 'company-tags',
  Compliances = 'compliances',
  ContentItems = 'content-items',
  CountEntitiesByTag = 'count-entities-by-tag',
  CreateProjectListingSuggestions = 'create-project-listing-suggestions',
  DeploymentModels = 'deployment-models',
  EmployeeRanges = 'employee-ranges',
  ExploreHistory = 'explore-history',
  ExploreRequestHistoryFormData = 'explore-request-history-form-data',
  ExploreRequestHistorySuggestions = 'explore-request-history-suggestions',
  ExploreSuggestions = 'explore-suggestions',
  FeedContentItems = 'feed-content-items',
  FundingStages = 'funding-stages',
  Funnel = 'funnel',
  Impacts = 'impacts',
  Integrations = 'integrations',
  HqLocations = 'hq-locations',
  InvitedUserData = 'invited-user-data',
  InviteToAccountLink = 'invite-to-account-link',
  Languages = 'languages',
  LastActivities = 'last-activities',
  MeetingLink = 'meeting-link',
  Notifications = 'notifications',
  NotificationCommentTagEntity = 'notification-comment-tag-entity',
  LastActivitiesCount = 'last-activities-count',
  ListingStages = 'listing-stages',
  Locations = 'locations',
  OwnershipStatuses = 'ownership-statuses',
  Project = 'project',
  ProjectActionsInfo = 'project-actions-info',
  ProjectActivity = 'project-activity',
  ProjectComments = 'project-comments',
  ProjectCompanies = 'project-companies',
  ProjectCreators = 'project-creators',
  ProjectExternalLinks = 'project-external-links',
  ProjectFileScanningStatus = 'project-file-scanning-status',
  ProjectFiles = 'project-files',
  ProjectListings = 'project-listings',
  ProjectListingById = 'project-listing-by-id',
  ProjectListingProjectFit = 'project-listing-project-fit',
  ProjectListingsCount = 'project-listings-count',
  ProjectMeetings = 'project-meetings',
  ProjectOptions = 'project-options',
  ProjectFiltersTeamOptions = 'project-filters-team-options',
  ProjectUpdates = 'project-updates',
  ProjectSearchResults = 'project-search-results',
  ProjectEnrichedSearchSuggestions = 'project-enriched-search-suggestions',
  ProjectTypes = 'project-types',
  Projects = 'projects',
  ProjectsKpi = 'projects-kpi',
  PendingProjectRequests = 'pending-project-requests',
  PendingProjectRequestsCount = 'pending-project-requests-count',
  PendingCompanies = 'pending-companies',
  PendingCompaniesCount = 'pending-companies-count',
  RecentItems = 'recent-items',
  RequestProjectUrl = 'request-project-url',
  RequestProjectSubmissionLink = 'request-project-submission-link',
  Roles = 'roles',
  Search = 'search',
  SharedCompanies = 'shared-companies',
  SharedProjects = 'shared-projects',
  ShareLinkValidation = 'share-link-validation',
  Snapshot = 'snapshot',
  SpotlightUrl = 'spotlight-url',
  Tags = 'tags',
  Team = 'team',
  TeamActionsInfo = 'team-actions-info',
  TeamComments = 'team-comment',
  TeamContacts = 'team-contacts',
  TeamFileScanningStatus = 'team-file-scanning-status',
  TeamFiles = 'team-files',
  TeamMeetings = 'team-meetings',
  TeamMembers = 'team-members',
  TeamOptions = 'team-options',
  TeamTags = 'team-tags',
  Teams = 'teams',
  Templates = 'templates',
  TemplateScanningStatus = 'template-scanning-status',
  UserRecentSearchRecords = 'user-recent-search-records',
  Users = 'users',
  UsersAll = 'users-all',
  UsersCount = 'users-count',
  UsersToMentionList = 'users-to-mention-list',
  McpOAuthGrants = 'mcp-oauth-grants',
  UserApiKeys = 'user-api-keys',
  WhoAmI = 'who-am-i',
}

export enum MutationKey {
  CreateCompanyFromCrunchbase = 'create-company-from-crunchbase',
  CreateExploreRound = 'create-explore-round',
  CompanyFileShare = 'company-file-share',
  CompanyFileUnshare = 'company-file-unshare',
  TeamFileShare = 'team-file-share',
  TeamFileUnshare = 'team-file-unshare',
}
